import api, { ActiveInactiveEnum, MICompanyRelationship, PermissionType } from '@api';
import { Edit, RemoveCircleOutline, Save } from '@mui/icons-material';
import { Paper, Typography } from '@mui/material';
import {
    IconButton, LabelGroup, LabeledValue, PhoneTypography
} from '@tsp-ui/core/components';
import { replaceItemById, usePageMessage, useParams } from '@tsp-ui/core/utils';
import { tooltipTitle, useHasPermission } from '@utils';
import { useGetCurrentAccount } from '@utils/hooks/useGetCurrentAccount';
import { AdminRouteParams } from '@views/admin/components/AdminPageTemplate';
import { Dispatch, SetStateAction, useState } from 'react';

import styles from './MIRelationshipCard.module.scss';
import { MIRelationshipForm } from './MIRelationshipForm';


interface MIRelationshipCardProps {
    miRelationship: MICompanyRelationship | undefined;
    setAdding?: Dispatch<SetStateAction<boolean>>;
    setMIRelationships: Dispatch<SetStateAction<MICompanyRelationship[]>>;
    locked?: boolean;
}

export function MIRelationshipCard({
    miRelationship, setAdding, setMIRelationships, locked
}: MIRelationshipCardProps) {
    const [ editing, setEditing ] = useState(!miRelationship);
    const [ deleting, setDeleting ] = useState(false);
    const [ saving, setSaving ] = useState(false);
    const pageMessage = usePageMessage();

    const { id: clientID, customerId } = useGetCurrentAccount();
    const { customerID } = useParams<AdminRouteParams<'customer'>>();

    const [ canManage ] = useHasPermission([ PermissionType.MANAGE_MI_COMPANY_RELATIONSHIP ]);

    async function handleDelete() {
        setDeleting(true);

        if (miRelationship) {
            try {
                const deactivatedMIRelationship = await api.customer.miCompanyRelationship
                    .softDeleteMICompanyRelationship(clientID, customerId || customerID, miRelationship);

                setMIRelationships(miRelationships => replaceItemById(miRelationships, deactivatedMIRelationship));

                pageMessage.success('Mortgage insurance relationship deleted');
            } catch (error) {
                pageMessage.handleApiError('An error occurred while deleting the mortgage insurance relationship', error);
            }
        } else {
            setAdding?.(false);
        }

        setDeleting(false);
    }

    async function handleSubmit(formValues: MICompanyRelationship) {
        setSaving(true);

        try {
            const newMiRelationship = miRelationship
                ? await api.customer.miCompanyRelationship.updateMICompanyRelationship(
                    clientID, customerId || customerID, formValues
                )
                : await api.customer.miCompanyRelationship.createMICompanyRelationship(
                    clientID, customerId || customerID, {
                        ...formValues,
                        status: ActiveInactiveEnum.ACTIVE
                    }
                );

            if (miRelationship) {
                setMIRelationships(miRelationships => replaceItemById(miRelationships, newMiRelationship));
                setEditing(false);
            } else {
                setMIRelationships(miRelationships => [ ...miRelationships, newMiRelationship ]);
                setAdding?.(false);
            }

            pageMessage.success('Mortgage insurance company relationship saved');
        } catch (error) {
            pageMessage.handleApiError('An error occurred while saving the mortgage insurance company relationship', error);
        }

        setSaving(false);
    }

    return (
        <Paper
            className={styles.root}
            variant="outlined"
        >
            <div className={styles.displayHeader}>
                <Typography>
                    {miRelationship?.insurerName || 'New Insurance Relationship'}
                </Typography>

                <div>
                    {editing ? (
                        <IconButton
                            tooltip="Save mortgage insurance relationship"
                            form={`mi-relationship-${miRelationship?.id || 'new'}`}
                            type="submit"
                            loading={saving}
                        >
                            <Save color="success" />
                        </IconButton>
                    ) : (
                        <IconButton
                            tooltip={tooltipTitle({
                                'You must unlock this data request to make changes': locked,
                                'Edit mortgage insurance relationship': canManage,
                                'You do not have permission to edit mortgage insurance relationships': !canManage
                            })}
                            disabled={!canManage || locked}
                            onClick={() => setEditing(true)}
                            key="edit"
                        >
                            <Edit color="primary" />
                        </IconButton>
                    )}

                    <IconButton
                        tooltip={tooltipTitle({
                            'You must unlock this data request to make changes': locked,
                            'Delete mortgage insurance relationship': canManage,
                            'You do not have permission to delete mortgage insurance relationships': !canManage
                        })}
                        disabled={!canManage || locked}
                        loading={deleting}
                        onClick={handleDelete}
                        edge="end"
                    >
                        <RemoveCircleOutline color="error" />
                    </IconButton>
                </div>
            </div>

            {editing ? (
                <MIRelationshipForm
                    defaultValues={miRelationship}
                    handleSubmit={handleSubmit}
                />
            ) : (
                <LabelGroup>
                    <LabeledValue
                        label="Policy number:"
                        value={miRelationship?.policyNumber}
                    />

                    <LabeledValue
                        label="Contact name:"
                        value={miRelationship?.contact}
                    />

                    <LabeledValue
                        label="Phone #:"
                        value={(
                            <PhoneTypography variant="body2">
                                {miRelationship?.phone || ''}
                            </PhoneTypography>
                        )}
                    />

                    <LabeledValue
                        label="Email:"
                        value={miRelationship?.emailAddress}
                    />
                </LabelGroup>
            )}
        </Paper>
    );
}
